import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Shield, KeyRound, Info, Check, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

// Panel för att hantera signeringsmetod (BankID vs PIN) samt sätta/byta PIN.
// Visas överst på BillingProfiles-sidan.
export default function SigningMethodPanel() {
  const [method, setMethod] = useState('bankid');
  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSetPin, setShowSetPin] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const [pinNew, setPinNew] = useState('');
  const [pinCurrent, setPinCurrent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchMethod = async () => {
    setLoading(true);
    try {
      const res = await api.getSigningMethod();
      setMethod(res.data.signing_method || 'bankid');
      setHasPin(!!res.data.has_pin);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMethod(); }, []);

  const handleSetPin = async () => {
    if (!/^\d{4,8}$/.test(pinNew)) {
      toast.error('PIN måste vara 4–8 siffror');
      return;
    }
    setSaving(true);
    try {
      await api.setSigningPin({ pin: pinNew });
      toast.success('PIN-kod satt!');
      setPinNew('');
      setShowSetPin(false);
      await fetchMethod();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte sätta PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePin = async () => {
    if (!/^\d{4,8}$/.test(pinNew)) {
      toast.error('Ny PIN måste vara 4–8 siffror');
      return;
    }
    if (!/^\d{4,8}$/.test(pinCurrent)) {
      toast.error('Ange nuvarande PIN');
      return;
    }
    setSaving(true);
    try {
      await api.changeSigningPin({ current_pin: pinCurrent, new_pin: pinNew });
      toast.success('PIN uppdaterad!');
      setPinNew('');
      setPinCurrent('');
      setShowChangePin(false);
      await fetchMethod();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte byta PIN');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-5" data-testid="signing-method-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold flex items-center gap-2 mb-1">
            <Shield size={16} className="text-[#E8613A]" />
            Signeringsmetod för fakturor
          </h3>
          <p className="text-sm text-muted-foreground">
            {method === 'pin'
              ? 'Fakturor signeras med PIN-kod. Bra alternativ när BankID inte krävs eller inte finns tillgängligt.'
              : 'Fakturor signeras med BankID via IDURA.'}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
            method === 'pin' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
          }`}>
            {method === 'pin' ? <KeyRound size={12} /> : <Shield size={12} />}
            {method === 'pin' ? 'PIN-kod' : 'BankID'}
          </span>
          <p className="text-[11px] text-muted-foreground mt-1">
            {method === 'pin' && hasPin && <span className="flex items-center gap-1 justify-end"><Check size={10} /> PIN är satt</span>}
            {method === 'pin' && !hasPin && <span className="text-amber-700">PIN inte satt än</span>}
          </p>
        </div>
      </div>

      {method === 'pin' && (
        <div className="mt-4 flex flex-wrap gap-2">
          {!hasPin ? (
            <Button
              onClick={() => setShowSetPin(true)}
              className="bg-[#E8613A] hover:bg-[#D4532F] text-white text-sm h-8"
              data-testid="set-pin-btn"
            >
              <KeyRound size={13} className="mr-1.5" /> Skapa PIN-kod
            </Button>
          ) : (
            <Button
              onClick={() => setShowChangePin(true)}
              variant="outline"
              className="text-sm h-8"
              data-testid="change-pin-btn"
            >
              <KeyRound size={13} className="mr-1.5" /> Byt PIN
            </Button>
          )}
        </div>
      )}

      {method === 'bankid' && (
        <div className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          <span>Kontakta Operio-support om du vill byta till PIN-kod.</span>
        </div>
      )}

      {/* Set PIN Dialog */}
      {showSetPin && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={() => !saving && setShowSetPin(false)}>
          <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="set-pin-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-2">
              <KeyRound size={16} className="text-[#E8613A]" /> Skapa PIN-kod
            </h3>
            <p className="text-xs text-muted-foreground mb-3">Välj en 4–8-siffrig PIN som ska användas för att signera fakturor.</p>
            <Label className="text-xs">Ny PIN *</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={pinNew}
              onChange={(e) => setPinNew(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 mb-4 tracking-widest text-center text-lg font-mono"
              placeholder="••••"
              autoFocus
              data-testid="new-pin-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSetPin(false)} className="flex-1" disabled={saving}>Avbryt</Button>
              <Button onClick={handleSetPin} disabled={saving || pinNew.length < 4} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-set-pin">
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Spara PIN'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change PIN Dialog */}
      {showChangePin && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={() => !saving && setShowChangePin(false)}>
          <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="change-pin-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-3">
              <KeyRound size={16} className="text-[#E8613A]" /> Byt PIN
            </h3>
            <Label className="text-xs">Nuvarande PIN *</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={pinCurrent}
              onChange={(e) => setPinCurrent(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 mb-3 tracking-widest text-center text-lg font-mono"
              placeholder="••••"
              data-testid="current-pin-input"
            />
            <Label className="text-xs">Ny PIN *</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={pinNew}
              onChange={(e) => setPinNew(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 mb-4 tracking-widest text-center text-lg font-mono"
              placeholder="••••"
              data-testid="new-pin-input-change"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowChangePin(false)} className="flex-1" disabled={saving}>Avbryt</Button>
              <Button onClick={handleChangePin} disabled={saving} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-change-pin">
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Byt PIN'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
